import { BarChartWidget } from "../charts/BarChartWidget";
import { DonutChartWidget } from "../charts/DonutChartWidget";
import { HorizontalBarWidget } from "../charts/HorizontalBarWidget";
import { ReportSection } from "../ReportSection";
import { ReportTable, type TableColumn } from "../ReportTable";
import { StatCard } from "../StatCard";
import { fmtCurrency, fmtPercent } from "./formatters";

interface ExpenseAnalysisData {
  byCategory: Array<{
    category: string;
    subcategory: string | null;
    total: number;
    count: number;
    percent: number;
  }>;
  byProject: Array<{ projectName: string; total: number; count: number; percent: number }>;
  byMonth: Array<{ month: string; year: number; total: number; count: number }>;
  byPerson: Array<{ name: string; total: number; count: number }>;
  byStatus: {
    pending: number;
    approved: number;
    rejected: number;
    pendingAmount: number;
    approvedAmount: number;
  };
  topExpenses: Array<{
    id: string;
    code: string;
    title: string;
    amount: number;
    currency: string;
    category: string | null;
    projectName: string | null;
    submittedByName: string | null;
    expenseDate: string;
  }>;
  summary: {
    total: number;
    count: number;
    avgAmount: number;
    largestExpense: number;
    mostActiveCategory: string | null;
  };
}

interface CategoryRow {
  category: string;
  subcategory: string | null;
  total: number;
  count: number;
  percent: number;
}

export function ExpenseAnalysisReport({ data }: { data: ExpenseAnalysisData }) {
  const donutData = data.byCategory.slice(0, 8).map((c) => ({
    name: c.category,
    value: c.total,
  }));
  const monthData = data.byMonth.map((m) => ({
    month: m.month,
    Total: m.total,
  }));
  const projectData = data.byProject.slice(0, 10).map((p) => ({
    name: p.projectName,
    value: p.total,
  }));

  const cols: Array<TableColumn<CategoryRow>> = [
    { key: "category", label: "Category", render: (r) => r.category },
    {
      key: "subcategory",
      label: "Subcategory",
      render: (r) => r.subcategory ?? "—",
    },
    { key: "count", label: "Count", align: "right" },
    {
      key: "total",
      label: "Total Amount",
      align: "right",
      render: (r) => fmtCurrency(r.total),
    },
    {
      key: "percent",
      label: "% of Total",
      align: "right",
      render: (r) => fmtPercent(r.percent),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <StatCard label="Total Expenses" value={data.summary.count} />
        <StatCard label="Total Amount" value={fmtCurrency(data.summary.total)} />
        <StatCard
          label="Approved"
          value={data.byStatus.approved}
          hint={fmtCurrency(data.byStatus.approvedAmount)}
          tone="good"
        />
        <StatCard
          label="Pending"
          value={data.byStatus.pending}
          hint={fmtCurrency(data.byStatus.pendingAmount)}
          tone="warn"
        />
        <StatCard label="Rejected" value={data.byStatus.rejected} tone="bad" />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <ReportSection title="By Category">
          <DonutChartWidget data={donutData} formatter={(v) => fmtCurrency(v)} />
        </ReportSection>
        <ReportSection title="By Month">
          <BarChartWidget
            data={monthData}
            xKey="month"
            bars={[{ key: "Total", name: "Total", color: "hsl(210,100%,50%)" }]}
            yFormatter={(v) => fmtCurrency(v)}
          />
        </ReportSection>
      </div>

      <ReportSection title="By Project (top 10 by spend)">
        <HorizontalBarWidget
          data={projectData}
          yKey="name"
          xKey="value"
          xFormatter={(v) => fmtCurrency(v)}
          height={Math.max(160, projectData.length * 32)}
          color="hsl(270,75%,55%)"
        />
      </ReportSection>

      <ReportSection title="Category Breakdown">
        <ReportTable
          columns={cols}
          rows={data.byCategory}
          rowKey={(r, i) => `${r.category}-${r.subcategory ?? ""}-${i}`}
          empty="No expense data."
        />
      </ReportSection>
    </div>
  );
}
